import { useCreateEnvironmentLogsToken, type PlainEnvironment } from "@/queries/environment-queries";
import { useCreatePrebuildLogsToken, type PlainPrebuild } from "@/queries/prebuild-queries";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { PrebuildPhase } from "gitpod-next-api/gitpod/v1/prebuild_pb";
import type { LogStreamAdapter } from "./types";

/**
 * Adapter for streaming environment logs
 */
export const environmentAdapter: LogStreamAdapter<PlainEnvironment> = {
    getLogsUrl: (env): string | undefined => env?.status?.environmentUrls?.logs,
    
    getResourceId: (env): string | undefined => env?.id,
    
    getPhase: (env): number | undefined => env?.status?.phase,
    
    isEarlyPhase: (phase) => {
        if (!phase) return true;
        return (
            (phase as EnvironmentPhase) === EnvironmentPhase.CREATING ||
            (phase as EnvironmentPhase) === EnvironmentPhase.STARTING
        );
    },
    
    useLogsToken: (id, _phase) => {
        const result = useCreateEnvironmentLogsToken(id);
        return {
            data: result.data,
            isPending: result.isPending,
            error: result.error ?? undefined,
        };
    },
};

/**
 * Adapter for streaming prebuild logs
 */
export const prebuildAdapter: LogStreamAdapter<PlainPrebuild> = {
    getLogsUrl: (prebuild): string | undefined => prebuild?.status?.logUrl,
    
    getResourceId: (prebuild): string | undefined => prebuild?.id,
    
    getPhase: (prebuild): number | undefined => prebuild?.status?.phase,
    
    isEarlyPhase: (phase) => {
        if (!phase) return true;
        return (
            (phase as PrebuildPhase) === PrebuildPhase.PENDING ||
            (phase as PrebuildPhase) === PrebuildPhase.STARTING
        );
    },
    
    useLogsToken: (id, phase) => {
        const result = useCreatePrebuildLogsToken(id, phase);
        return {
            data: result.data,
            isPending: result.isPending,
            error: result.error ?? undefined,
        };
    },
};
